import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useAlerts } from "../../../hooks/useAlerts";

const TYPE_COLORS = {
    danger: { bg: "rgba(255,59,48,0.08)", border: "#ff3b30", icon: "#ff3b30" },
    warning: { bg: "rgba(255,149,0,0.08)", border: "#ff9500", icon: "#ff9500" },
    success: { bg: "rgba(52,199,89,0.08)", border: "#34c759", icon: "#34c759" },
    info: { bg: "rgba(0,122,255,0.08)", border: "#007aff", icon: "#007aff" },
};

const formatTimeAgo = (date) => {
    if (!date) return "";
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return "agora";
    if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    return `${Math.floor(diff / 86400)}d atrás`;
};

const NotificationDropdown = () => {
    const [menu, setMenu] = useState(false);
    const { alerts, unreadCount, dismissAlert, markAsRead, clearAll } = useAlerts();

    const handleToggle = () => {
        if (!menu && unreadCount > 0) markAsRead();
        setMenu(!menu);
    };

    return (
        <Dropdown
            isOpen={menu}
            toggle={handleToggle}
            className="d-inline-block"
        >
            <DropdownToggle
                className="btn header-item noti-icon waves-effect position-relative"
                tag="button"
                id="page-header-notifications-dropdown"
            >
                <i className="mdi mdi-bell-outline font-size-22"></i>
                {unreadCount > 0 && (
                    <span
                        className="position-absolute d-flex align-items-center justify-content-center"
                        style={{
                            top: 12,
                            right: 10,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            backgroundColor: "#ff3b30",
                            color: "#fff",
                            fontSize: "10px",
                            fontWeight: 700,
                            border: "2px solid #020308",
                            zIndex: 1,
                        }}
                    >
                        {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                )}
            </DropdownToggle>

            <DropdownMenu
                className="dropdown-menu-end shadow-lg p-0"
                container="body"
                style={{
                    minWidth: 340,
                    maxWidth: 380,
                    backgroundColor: "#020308",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    borderTop: "none",
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomLeftRadius: 16,
                    borderBottomRightRadius: 16,
                    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.5)",
                    marginTop: "0.5px",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <div className="px-3 py-3 d-flex align-items-center justify-content-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <h6 className="m-0 fw-bold text-white font-size-14 d-flex align-items-center gap-2">
                        <i className="mdi mdi-bell-outline" style={{ color: "#00d1ff" }}></i>
                        Alertas
                        {unreadCount > 0 && (
                            <span className="badge rounded-pill" style={{ backgroundColor: "#ff3b30", fontSize: 10 }}>
                                {unreadCount}
                            </span>
                        )}
                    </h6>
                    {alerts.length > 0 && (
                        <button
                            className="btn btn-sm p-0 font-size-12"
                            style={{ color: "rgba(255,255,255,0.4)" }}
                            onClick={(e) => { e.stopPropagation(); clearAll(); }}
                        >
                            Limpar
                        </button>
                    )}
                </div>

                {/* Alertas */}
                {alerts.length === 0 ? (
                    <div className="px-4 py-4 text-center">
                        <i className="mdi mdi-bell-check-outline font-size-36 d-block mb-2" style={{ color: "rgba(255,255,255,0.15)" }}></i>
                        <p className="mb-0 font-size-13" style={{ color: "rgba(255,255,255,0.4)" }}>Nenhum alerta no momento</p>
                    </div>
                ) : (
                    <SimpleBar style={{ maxHeight: 320 }}>
                        {alerts.map((alert) => {
                            const colors = TYPE_COLORS[alert.type] || TYPE_COLORS.info;
                            return (
                                <div
                                    key={alert.id}
                                    className="px-3 py-3 position-relative"
                                    style={{
                                        backgroundColor: !alert.read ? colors.bg : "transparent",
                                        borderLeft: `3px solid ${colors.border}`,
                                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                                        transition: "background-color 0.3s",
                                    }}
                                >
                                    <button
                                        className="btn btn-sm p-0 position-absolute"
                                        style={{ top: 8, right: 10, lineHeight: 1, color: "rgba(255,255,255,0.3)" }}
                                        onClick={(e) => { e.stopPropagation(); dismissAlert(alert.id); }}
                                        title="Dispensar"
                                    >
                                        <i className="mdi mdi-close font-size-14"></i>
                                    </button>

                                    <div className="d-flex align-items-start">
                                        <div
                                            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 me-3"
                                            style={{
                                                width: 32,
                                                height: 32,
                                                backgroundColor: `${colors.border}20`,
                                            }}
                                        >
                                            <i
                                                className={`mdi ${alert.icon || "mdi-alert-circle"} font-size-16`}
                                                style={{ color: colors.icon }}
                                            ></i>
                                        </div>
                                        <div className="flex-grow-1 overflow-hidden pe-3">
                                            <h6 className="mb-1 font-size-13 fw-semibold text-white text-truncate">
                                                {alert.title}
                                            </h6>
                                            <p className="mb-1 font-size-12" style={{ color: "rgba(255,255,255,0.5)", lineHeight: 1.4 }}>
                                                {alert.message}
                                            </p>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
                                                    {formatTimeAgo(alert.timestamp)}
                                                </span>
                                                {alert.link && (
                                                    <Link
                                                        to={alert.link}
                                                        className="font-size-11 fw-semibold"
                                                        style={{ color: "#00d1ff" }}
                                                        onClick={() => setMenu(false)}
                                                    >
                                                        {alert.linkText || "Ver detalhes"}
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </SimpleBar>
                )}
            </DropdownMenu>
        </Dropdown>
    );
};

export default NotificationDropdown;